import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { SlideOver, SaveStatus } from "./SlideOver";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Armchair, DoorOpen, Presentation, Wind, Accessibility as AccessIcon,
  Ban, User as UserIcon, Save, Grid3x3, Sparkles, RotateCcw,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

// Cell states cycle: empty → seat → blocked → back to empty for click behaviour.
// Fixture placement uses a separate "tool" selection.
const TOOLS = [
  { key: "seat",      label: "Seat",       icon: Armchair,      tone: "sky",    fill: "bg-sky-100 text-sky-700 border-sky-200" },
  { key: "blocked",   label: "Blocked",    icon: Ban,           tone: "rose",   fill: "bg-rose-100 text-rose-700 border-rose-200" },
  { key: "teacher",   label: "Teacher",    icon: UserIcon,      tone: "amber",  fill: "bg-amber-100 text-amber-700 border-amber-200" },
  { key: "projector", label: "Projector",  icon: Presentation,  tone: "violet", fill: "bg-violet-100 text-violet-700 border-violet-200" },
  { key: "door",      label: "Door",       icon: DoorOpen,      tone: "emerald",fill: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  { key: "exit",      label: "Emerg. Exit",icon: DoorOpen,      tone: "orange", fill: "bg-orange-100 text-orange-700 border-orange-200" },
  { key: "window",    label: "Window",     icon: Wind,          tone: "cyan",   fill: "bg-cyan-100 text-cyan-700 border-cyan-200" },
  { key: "wheelchair",label: "Wheelchair", icon: AccessIcon,    tone: "teal",   fill: "bg-teal-100 text-teal-700 border-teal-200" },
  { key: "empty",     label: "Erase",      icon: RotateCcw,     tone: "slate",  fill: "bg-slate-50 text-slate-500 border-slate-200" },
];

function toolFor(key) { return TOOLS.find((t) => t.key === key) || TOOLS[TOOLS.length - 1]; }

function makeGrid(rows, cols) {
  return Array.from({ length: rows }, () => Array.from({ length: cols }, () => ({ type: "empty" })));
}

function autoNumber(grid) {
  let n = 1;
  return grid.map((row) => row.map((cell) => (cell.type === "seat" ? { ...cell, num: n++ } : { ...cell, num: undefined })));
}

export function RoomLayoutEditor({ open, onClose, room, onSaved }) {
  const qc = useQueryClient();
  const [rows, setRows] = useState(6);
  const [cols, setCols] = useState(8);
  const [grid, setGrid] = useState(() => makeGrid(6, 8));
  const [tool, setTool] = useState("seat");
  const [status, setStatus] = useState("idle");

  // Load existing layout when the panel opens for a room.
  useEffect(() => {
    if (!open || !room) return;
    const layout = room.layout;
    if (layout?.rows && layout?.cols && Array.isArray(layout?.grid)) {
      setRows(layout.rows);
      setCols(layout.cols);
      setGrid(layout.grid);
    } else {
      const r = 6, c = 8;
      setRows(r); setCols(c); setGrid(makeGrid(r, c));
    }
  }, [open, room?.id]);

  const resize = (newRows, newCols) => {
    setRows(newRows); setCols(newCols);
    setGrid((g) => {
      const next = makeGrid(newRows, newCols);
      for (let r = 0; r < Math.min(newRows, g.length); r++) {
        for (let c = 0; c < Math.min(newCols, g[r].length); c++) {
          next[r][c] = g[r][c];
        }
      }
      return next;
    });
  };

  const paint = (r, c) => {
    setGrid((g) => {
      const next = g.map((row) => row.slice());
      next[r][c] = { type: tool };
      return next;
    });
  };

  const fillSeats = () => {
    setGrid(() => Array.from({ length: rows }, () => Array.from({ length: cols }, () => ({ type: "seat" }))));
  };

  const clear = () => setGrid(makeGrid(rows, cols));

  const save = async () => {
    if (!room?.id) return;
    const numbered = autoNumber(grid);
    const seatCount = numbered.flat().filter((c) => c.type === "seat").length;
    setStatus("saving");
    try {
      await api.patch(`/infrastructure/rooms/${room.id}`, {
        layout: { rows, cols, grid: numbered, seat_count: seatCount },
        exam_capacity: seatCount,
      });
      setStatus("saved");
      toast.success(`Saved layout — ${seatCount} seats mapped`);
      qc.invalidateQueries({ queryKey: ["infra-rooms"] });
      qc.invalidateQueries({ queryKey: ["infra-dashboard"] });
      onSaved?.();
      setTimeout(() => setStatus("idle"), 1200);
    } catch (err) {
      setStatus("error");
      toast.error(err?.response?.data?.detail || "Save failed");
    }
  };

  const seatCount = grid.flat().filter((c) => c.type === "seat").length;

  return (
    <SlideOver
      open={open}
      onClose={onClose}
      title={`Room Layout · ${room?.name || ""}`}
      subtitle={`Click cells to paint. ${seatCount} seat${seatCount === 1 ? "" : "s"} mapped so far.`}
      right={
        <div className="flex items-center gap-2">
          <SaveStatus status={status} />
          <Button size="sm" onClick={save} data-testid="layout-save">
            <Save className="h-4 w-4 mr-1" /> Save Layout
          </Button>
        </div>
      }
      width="max-w-6xl"
      testid="room-layout"
    >
      <div className="p-6 space-y-4">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Label className="text-xs text-slate-600">Rows</Label>
            <Input
              type="number" min={1} max={30} value={rows}
              onChange={(e) => resize(Math.max(1, Math.min(30, Number(e.target.value) || 1)), cols)}
              className="w-20" data-testid="layout-rows"
            />
            <Label className="text-xs text-slate-600 ml-2">Cols</Label>
            <Input
              type="number" min={1} max={30} value={cols}
              onChange={(e) => resize(rows, Math.max(1, Math.min(30, Number(e.target.value) || 1)))}
              className="w-20" data-testid="layout-cols"
            />
          </div>
          <div className="flex items-center gap-2 ml-auto">
            <Button size="sm" variant="outline" onClick={fillSeats} data-testid="layout-fill">
              <Grid3x3 className="h-3.5 w-3.5 mr-1" /> Fill with seats
            </Button>
            <Button size="sm" variant="outline" onClick={clear} data-testid="layout-clear">
              <RotateCcw className="h-3.5 w-3.5 mr-1" /> Clear
            </Button>
          </div>
        </div>

        {/* Toolbar */}
        <div className="flex flex-wrap gap-1.5 p-2 rounded-xl bg-slate-50 border border-slate-200">
          {TOOLS.map((t) => {
            const Icon = t.icon;
            const active = tool === t.key;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => setTool(t.key)}
                className={cn(
                  "px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1.5 border transition-all",
                  active ? `${t.fill} ring-2 ring-offset-1 ring-slate-900/10` : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                )}
                data-testid={`layout-tool-${t.key}`}
              >
                <Icon className="h-3.5 w-3.5" /> {t.label}
              </button>
            );
          })}
        </div>

        {/* Canvas */}
        <div className="rounded-xl border border-slate-200 bg-white p-4 overflow-auto">
          <div className="text-center text-[10px] uppercase tracking-widest font-semibold text-slate-400 mb-2">
            ← Front of the room →
          </div>
          <div
            className="grid gap-1.5 mx-auto w-fit"
            style={{ gridTemplateColumns: `repeat(${cols}, minmax(2.75rem, 3rem))` }}
            data-testid="layout-canvas"
          >
            {grid.map((row, r) =>
              row.map((cell, c) => {
                const t = toolFor(cell.type);
                const Icon = t.icon;
                return (
                  <button
                    key={`${r}-${c}`}
                    type="button"
                    onClick={() => paint(r, c)}
                    className={cn(
                      "aspect-square rounded-md border text-[10px] font-semibold flex flex-col items-center justify-center transition-colors",
                      cell.type === "empty" ? "bg-white border-slate-200 hover:bg-slate-50" : t.fill
                    )}
                    data-testid={`cell-${r}-${c}`}
                    title={`${t.label} · row ${r + 1}, col ${c + 1}`}
                  >
                    {cell.type !== "empty" && <Icon className="h-3 w-3" />}
                  </button>
                );
              })
            )}
          </div>
        </div>

        <div className="flex items-center justify-between text-xs text-slate-500 px-1">
          <div>Seats are auto-numbered on save (top-left → bottom-right).</div>
          <div className="flex items-center gap-1.5 text-slate-600">
            <Sparkles className="h-3 w-3 text-violet-500" />
            Saving updates the room&apos;s <b>exam capacity</b> so the Exam module can plan seat maps.
          </div>
        </div>
      </div>
    </SlideOver>
  );
}
